import StringSimilarity from "string-similarity";

import { ConceptMatch } from "@/tools/msa/models/concepts";
import { getNames } from "@/tools/msa/utils/names/index";
import { Session } from "@/tools/msa/models";

const FORBIDDEN_WORDS = ["DOB", "Provider", "Patient", "Hospital"];

const isForbiddenWord = (text: string) =>
  StringSimilarity.findBestMatch(
    text.toLocaleLowerCase(),
    FORBIDDEN_WORDS.map(word => word.toLocaleLowerCase())
  ).ratings.some(match => match.rating > 0.65);

const containsForbiddenWord = (text: string) =>
  text.split(/\s+/).some(word => isForbiddenWord(word));

export const extractPatientNames = (text: string) =>
  [...text.matchAll(/Patient( Name)?:?\s*([A-Za-z]{2}[ A-Za-z-]*[A-Za-z])/g)]
    .map(match => match[2])
    .filter(Boolean)
    .filter(match => PATIENT_NAME_FILTERS.every(filter => filter(match)));

export const getAllPatientNames = (session: Session) =>
  [
    ...new Set([
      ...extractPatientNames(
        session.documents
          .map(document =>
            document.tokens
              .map(token => `${token.text}${token.delimiter}`)
              .join()
          )
          .join()
      ),
      ...session.documents
        .flatMap(document =>
          getNames(document, "name.patname", PATIENT_NAME_MATCH_FILTERS)
        )
        .filter(
          fullName =>
            fullName.split(/\s+/).filter(name => name.length >= 2).length >= 2
        )
    ])
  ]
    .map(
      text =>
        [
          text,
          PATIENT_NAME_FILTERS.map(filter => filter(text)).filter(Boolean)
            .length
        ] as [string, number]
    )
    .sort((a, b) => b[1] - a[1])
    .map(([text]) => text);

export const PATIENT_NAME_FILTERS: Array<(text: string) => boolean> = [
  (text: string) =>
    text
      .split(/\s+/)
      .filter(name => !isForbiddenWord(name))
      .filter(name => name.length >= 3).length >= 2,
  (text: string) => !containsForbiddenWord(text),
  (text: string) => [":", "\t"].some(token => text.includes(token))
];

export const PATIENT_NAME_MATCH_FILTERS: Array<
  (match: ConceptMatch) => boolean
> = PATIENT_NAME_FILTERS.map(filter => (match: ConceptMatch) =>
  filter(match.text)
);
